/**
 * ProgrammeOfficiel.jsx — Vue publique du programme officiel du permis AM.
 * Affiche les 5 séquences pédagogiques du référentiel Codes Rousseau avec :
 *   - Objectifs officiels de chaque séquence (A, B, C…)
 *   - Liens directs vers les chapitres du cours qui les couvrent
 * Route : /permis-am/programme
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, ArrowRight, CheckCircle2, ChevronDown, Clock,
  BookOpen, ShieldCheck, GraduationCap, Sparkles,
} from 'lucide-react';
import useSeo from '../../hooks/useSeo';
import { REFERENTIEL_AM } from '../../data/referentielAM';

const routeForSlug = (slug) => `/cours/${slug}`;

/* ─────────────────────────────────────────────────────────────
   Bloc séquence pliable
   ───────────────────────────────────────────────────────────── */
function Sequence({ seq, expanded, onToggle }) {
  const c = seq.color;
  return (
    <div
      className={`rounded-2xl border-2 ${c.border} bg-gradient-to-br ${c.bg} overflow-hidden transition-all`}
      data-testid={`sequence-${seq.code}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-4 sm:p-5 flex items-center gap-4 text-left active:scale-[0.99] transition"
        aria-expanded={expanded}
        data-testid={`sequence-toggle-${seq.code}`}
      >
        <div
          className={`flex-shrink-0 w-14 h-14 rounded-2xl ${c.solid} text-white flex flex-col items-center justify-center font-black shadow-lg`}
        >
          <span className="text-[10px] uppercase tracking-widest opacity-80 leading-none">Séq.</span>
          <span className="text-xl leading-none">{seq.code}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] font-black uppercase tracking-[0.22em] ${c.text} mb-1`}>
            {c.label} · {seq.duree}
          </div>
          <h3
            className="text-base sm:text-lg font-black leading-tight text-foreground"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {seq.title}
          </h3>
          <div className="text-xs text-muted-foreground mt-0.5">{seq.tagline}</div>
        </div>
        <ChevronDown
          className={`w-5 h-5 flex-shrink-0 transition-transform ${c.text} ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-3 animate-in fade-in duration-200">
          {seq.objectifs.map((obj) => (
            <div
              key={obj.code}
              className="rounded-xl border border-white/10 bg-black/30 p-3 sm:p-4"
              data-testid={`objectif-${seq.code}-${obj.code}`}
            >
              <div className="flex items-start gap-3 mb-2">
                <div
                  className={`flex-shrink-0 w-9 h-9 rounded-lg ${c.solid} text-white flex items-center justify-center font-black text-sm shadow`}
                >
                  {obj.code}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm sm:text-base font-black text-foreground leading-snug"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {obj.title}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {obj.desc}
                  </p>
                </div>
              </div>

              {obj.links && obj.links.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {obj.links.map((lk, i) => (
                    <Link
                      key={i}
                      to={routeForSlug(lk.slug)}
                      reloadDocument
                      className={`inline-flex items-center gap-1.5 rounded-lg border ${c.border} bg-black/40 hover:bg-black/60 hover:border-white/40 active:scale-95 px-2.5 py-1.5 text-[11px] font-bold text-foreground/90 transition-all`}
                      data-testid={`objectif-link-${seq.code}-${obj.code}-${i}`}
                    >
                      <BookOpen className={`w-3 h-3 ${c.text}`} />
                      {lk.label}
                      <ArrowRight className="w-3 h-3 opacity-60" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Page principale
   ───────────────────────────────────────────────────────────── */
export default function ProgrammeOfficiel() {
  const [expanded, setExpanded] = useState('S1');

  useSeo({
    title: 'Programme officiel du permis AM — Code 2 Roues',
    description:
      "Les 5 séquences pédagogiques du référentiel officiel permis AM (Codes Rousseau) : représentations, hors circulation, apports théoriques, circulation, sensibilisation aux risques.",
  });

  const toggle = (code) => setExpanded((cur) => (cur === code ? null : code));

  return (
    <div className="min-h-screen bg-background text-foreground pb-20" data-testid="programme-officiel-page">
      {/* Topbar */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-3 py-3 flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" data-testid="programme-back">
            <Link to="/permis-am" reloadDocument>
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <div className="flex-1 text-center">
            <div className="text-[9px] font-black uppercase tracking-[0.22em] text-red-400">
              Référentiel officiel
            </div>
            <div className="text-sm font-black leading-tight truncate">
              Programme du permis AM
            </div>
          </div>
          <div className="w-9" />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-3 sm:px-5 py-6 space-y-6">
        {/* HERO */}
        <header className="text-center space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-[10px] font-black uppercase tracking-widest text-red-300">
            <ShieldCheck className="w-3 h-3" /> Conforme référentiel officiel
          </div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="programme-title"
          >
            Programme officiel<br className="sm:hidden" />
            <span className="text-red-500"> Permis AM</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Toute notre formation suit strictement le référentiel officiel du permis AM (source :
            Codes Rousseau Formateur). Chaque séquence de notre cours est directement rattachée aux
            objectifs officiels que tu dois valider.
          </p>
        </header>

        {/* Encart durée totale */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[
            { label: 'DURÉE TOTALE', value: '8 h min', icon: Clock },
            { label: 'SÉQUENCES', value: '5', icon: BookOpen },
            { label: 'OBJECTIFS OFFICIELS', value: '28', icon: GraduationCap },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div
                key={i}
                className="rounded-xl border-2 border-white/10 bg-black/30 p-3 sm:p-4 text-center"
                data-testid={`kpi-${i}`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 text-red-400" />
                <div className="text-lg sm:text-2xl font-black tabular-nums" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {k.value}
                </div>
                <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-muted-foreground mt-0.5">
                  {k.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bandeau explicatif */}
        <div className="rounded-2xl border-2 border-red-500/30 bg-gradient-to-br from-red-950/40 to-black/40 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-300 mb-1">
                Comment lire ce programme
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed">
                Touche une séquence pour voir ses objectifs officiels. Chaque objectif renvoie vers
                les chapitres du cours qui le couvrent — tu peux vérifier à tout moment où tu en es
                dans la formation.
              </p>
            </div>
          </div>
        </div>

        {/* Les 5 séquences */}
        <section className="space-y-3" data-testid="sequences-list">
          {REFERENTIEL_AM.map((seq) => (
            <Sequence
              key={seq.code}
              seq={seq}
              expanded={expanded === seq.code}
              onToggle={() => toggle(seq.code)}
            />
          ))}
        </section>

        {/* Footer CTA */}
        <div className="rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-950/30 to-black/40 p-5 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
          <h2
            className="text-xl sm:text-2xl font-black leading-tight mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Tu retrouves tout ce contenu dans nos cours
          </h2>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            Nos formations Journée 1, Journée 2 et Sensibilisation couvrent l&apos;intégralité du
            référentiel officiel.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button asChild className="bg-red-600 hover:bg-red-500 text-white font-black" data-testid="programme-cta-am">
              <Link to="/permis-am" reloadDocument>
                Retour au hub AM <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
